/**
 * Арифметика перетаскивания очереди. Живёт отдельно от компонента, потому что
 * вся её суть — вычисления, которые нужно проверять тестами, а не разглядывать
 * пальцем на телефоне.
 */

export interface RowBox {
  /** Смещение строки от верха списка. */
  top: number;
  height: number;
}

/** Перестановка одного элемента. Возвращает новый массив, исходный не трогает. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [moved] = next.splice(from, 1);

  if (moved === undefined) {
    return next;
  }

  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

/**
 * Куда попадёт строка, которую сейчас тащат. Правило одно и его легко объяснить
 * вслух: строка занимает место соседа, когда её середина прошла его середину.
 * Считаем по списку без самой перетаскиваемой строки — это ровно тот индекс,
 * который потом ждёт `moveItem`.
 */
export function dropIndex(rows: readonly RowBox[], from: number, offset: number): number {
  const dragged = rows[from];

  if (!dragged) {
    return from;
  }

  const middle = dragged.top + offset + dragged.height / 2;
  let index = 0;

  for (let i = 0; i < rows.length; i += 1) {
    if (i === from) {
      continue;
    }

    const row = rows[i]!;
    if (row.top + row.height / 2 < middle) {
      index += 1;
    }
  }

  return index;
}

/**
 * Насколько сдвинуть чужую строку, чтобы на её месте открылся просвет. Двигаем
 * трансформацией, а не перестановкой в разметке: тогда размеры, измеренные в
 * начале жеста, остаются верными до самого конца.
 */
export function rowShift(index: number, from: number, to: number, step: number): number {
  if (index === from) {
    return 0;
  }

  if (to > from && index > from && index <= to) {
    return -step;
  }

  if (to < from && index >= to && index < from) {
    return step;
  }

  return 0;
}

/** Где окажется перетаскиваемая строка: туда же рисуется подсказка «сюда». */
export function dropTop(rows: readonly RowBox[], from: number, to: number): number {
  const dragged = rows[from];
  const target = rows[to];

  if (!dragged || !target) {
    return 0;
  }

  return to > from ? target.top + target.height - dragged.height : target.top;
}

/** Расстояние между строками: одинаковое, поэтому берём первое попавшееся. */
export function rowGap(rows: readonly RowBox[]): number {
  const first = rows[0];
  const second = rows[1];

  return first && second ? Math.max(0, second.top - (first.top + first.height)) : 0;
}
